import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FileText, Upload, Trash2, Eye, Calendar, Building2,
  Loader2, AlertCircle, CheckCircle, RefreshCw, Sparkles, 
  FileUp, Check, AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const MONTHS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

export default function BankStatements() {
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Filters
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(null);
  
  // Smart Import Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // View PDF
  const [viewingStatement, setViewingStatement] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  // Delete confirmation
  const [deleteStatement, setDeleteStatement] = useState(null);

  const fetchStatements = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      let url = `${API}/api/bank-statements?year=${filterYear}`;
      if (filterMonth) {
        url += `&month=${filterMonth}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStatements(response.data.statements || []);
    } catch (error) {
      console.error("Error fetching statements:", error);
      toast.error("Erreur lors du chargement des relevés");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.name.toLowerCase().endsWith('.pdf')
    );
    
    if (files.length > 0) {
      handleFilesSelected(files);
    } else {
      toast.error("Seuls les fichiers PDF sont acceptés");
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files).filter(
      f => f.name.toLowerCase().endsWith('.pdf')
    );
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    e.target.value = '';
  };

  const handleFilesSelected = (files) => {
    setAnalysisResults([]);
    setShowImportModal(true);
    analyzeFiles(files);
  };

  const analyzeFiles = async (files) => {
    setAnalyzing(true);
    setImportProgress(0);
    
    const token = localStorage.getItem("token");
    const results = [];
    
    // Délai entre chaque analyse pour éviter le rate limiting Gemini
    const DELAY_BETWEEN_ANALYSES = 3000; // 3 secondes
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setImportProgress(Math.round(((i) / files.length) * 100));
      
      // Attendre avant le prochain appel (sauf pour le premier)
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_ANALYSES));
      }
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(
          `${API}/api/bank-statements/analyze`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            },
            timeout: 60000 // 60 secondes timeout pour OCR
          }
        );
        
        results.push({
          file,
          analysis: response.data,
          status: response.data.period_month && response.data.period_year ? 'ready' : 'error',
          selected: response.data.period_month && response.data.period_year
        });
      } catch (error) {
        console.error(`Error analyzing ${file.name}:`, error);
        results.push({
          file,
          analysis: { error: error.response?.data?.detail || "Erreur d'analyse" },
          status: 'error',
          selected: false
        });
      }
    }
    
    setImportProgress(100);
    setAnalysisResults(results);
    setAnalyzing(false);
  };

  const handleImportSelected = async () => {
    const selectedFiles = analysisResults.filter(r => r.selected && r.status === 'ready');
    
    if (selectedFiles.length === 0) {
      toast.error("Aucun fichier sélectionné");
      return;
    }
    
    setUploading(true);
    const token = localStorage.getItem("token");
    
    let successCount = 0;
    let errorCount = 0;
    const errors = [];
    
    for (const item of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        // Use pre-extracted OCR data instead of re-running OCR
        formData.append("month", item.analysis.period_month);
        formData.append("year", item.analysis.period_year);
        formData.append("bank_name", item.analysis.bank_name || "Banque");
        formData.append("confidence", item.analysis.confidence || "medium");
        if (item.analysis.iban) {
          formData.append("iban", item.analysis.iban);
        }
        if (item.analysis.account_type) {
          formData.append("account_type", item.analysis.account_type);
        }
        
        await axios.post(
          `${API}/api/bank-statements/upload-with-metadata`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );
        
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${item.file.name}:`, error);
        errorCount++;
        errors.push(error.response?.data?.detail || item.file.name);
      }
    }
    
    setUploading(false);
    setShowImportModal(false);
    setAnalysisResults([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} relevé(s) importé(s) avec succès`);
      fetchStatements();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur(s): ${errors.join(', ')}`);
    }
  };

  const toggleFileSelection = (index) => {
    setAnalysisResults(prev => prev.map((r, i) => 
      i === index ? { ...r, selected: !r.selected } : r
    ));
  };

  const handleView = async (statement) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/api/bank-statements/${statement.statement_id}/view`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Revoke previous URL if exists
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      const url = URL.createObjectURL(response.data);
      setPdfUrl(url);
      setViewingStatement(statement);
    } catch (error) {
      console.error("Error viewing statement:", error);
      toast.error("Erreur lors de l'ouverture du fichier");
    }
  };

  const closePdfViewer = useCallback(() => {
    setViewingStatement(null);
    // Delay URL revocation to avoid React DOM issues
    setTimeout(() => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }, 100);
  }, [pdfUrl]);

  const handleDelete = async () => {
    if (!deleteStatement) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/bank-statements/${deleteStatement.statement_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Relevé supprimé");
      setDeleteStatement(null);
      fetchStatements();
    } catch (error) {
      console.error("Error deleting statement:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Fiable</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Moyen</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-700 border-red-200">Faible</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="bank-statements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relevés de banque</h1>
          <p className="text-gray-500 mt-1">
            Import intelligent avec détection automatique
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => fetchStatements()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Import intelligent
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
          }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`p-4 rounded-full ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <FileUp className={`w-8 h-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="font-medium text-gray-700">
              {isDragging ? "Déposez vos fichiers ici" : "Glissez-déposez vos relevés PDF"}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ou cliquez pour sélectionner • L'IA détecte automatiquement le mois et la banque
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-32">
              <Label className="text-sm text-gray-600">Année</Label>
              <Select
                value={filterYear.toString()}
                onValueChange={(val) => setFilterYear(parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Label className="text-sm text-gray-600">Mois</Label>
              <Select
                value={filterMonth?.toString() || "all"}
                onValueChange={(val) => setFilterMonth(val === "all" ? null : parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-sm text-gray-500">
              {statements.length} relevé{statements.length !== 1 ? 's' : ''} trouvé{statements.length !== 1 ? 's' : ''}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statements List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : statements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun relevé bancaire
            </h3>
            <p className="text-gray-500 mb-4">
              Importez vos relevés bancaires PDF pour les conserver et les inclure dans vos dossiers comptables.
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Sparkles className="w-4 h-4 mr-2" />
              Import intelligent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {statements.map((stmt) => (
            <Card key={stmt.statement_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-gray-900 truncate max-w-md">
                        {stmt.filename}
                      </h3>
                      {stmt.ocr_data?.confidence && getConfidenceBadge(stmt.ocr_data.confidence)}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {MONTHS.find(m => m.value === stmt.month)?.label} {stmt.year}
                      </span>
                      {stmt.bank_name && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {stmt.bank_name}
                        </span>
                      )}
                      <span>{formatFileSize(stmt.file_size)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(stmt)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteStatement(stmt)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Smart Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        if (!open && !analyzing && !uploading) {
          setShowImportModal(false);
          setAnalysisResults([]);
        }
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Import intelligent
            </DialogTitle>
            <DialogDescription>
              L'IA analyse vos relevés et détecte automatiquement la période et la banque
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[50vh] overflow-y-auto">
            {/* Progress bar during analysis */}
            {analyzing && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    Analyse IA en cours... {analysisResults.length > 0 && `(${analysisResults.length} analysé${analysisResults.length > 1 ? 's' : ''})`}
                  </span>
                  <span className="font-medium">{importProgress}%</span>
                </div>
                <Progress value={importProgress} className="h-2" />
                <p className="text-xs text-gray-400 text-center">
                  Chaque fichier est analysé individuellement par l'IA
                </p>
              </div>
            )}
            
            {/* Analysis Results */}
            {analysisResults.length > 0 && !analyzing && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {analysisResults.filter(r => r.selected).length} fichier(s) sélectionné(s)
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAnalysisResults(prev => 
                      prev.map(r => ({ ...r, selected: r.status === 'ready' }))
                    )}
                  >
                    Tout sélectionner
                  </Button>
                </div>
                
                {analysisResults.map((result, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border transition-all cursor-pointer ${
                      result.selected 
                        ? 'border-blue-300 bg-blue-50/50' 
                        : 'border-gray-200 hover:border-gray-300'
                    } ${result.status === 'error' ? 'opacity-60' : ''}`}
                    onClick={() => result.status === 'ready' && toggleFileSelection(index)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        result.selected 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-gray-300'
                      }`}>
                        {result.selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">{result.file.name}</span>
                          <span className="text-xs text-gray-400">{formatFileSize(result.file.size)}</span>
                        </div>
                        
                        {result.status === 'ready' ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-xs">
                              <Calendar className="w-3 h-3 mr-1" />
                              {MONTHS.find(m => m.value === result.analysis.period_month)?.label} {result.analysis.period_year}
                            </Badge>
                            {result.analysis.bank_name && (
                              <Badge variant="outline" className="text-xs">
                                <Building2 className="w-3 h-3 mr-1" />
                                {result.analysis.bank_name}
                              </Badge>
                            )}
                            {getConfidenceBadge(result.analysis.confidence)}
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm">{result.analysis.error || "Erreur d'analyse"}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-shrink-0">
                        {result.status === 'ready' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Actions - Fixed at bottom */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportModal(false);
                setAnalysisResults([]);
              }}
              disabled={analyzing || uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleImportSelected}
              disabled={analyzing || uploading || analysisResults.filter(r => r.selected).length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importer {analysisResults.filter(r => r.selected).length} fichier(s)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      {viewingStatement && (
        <Dialog open={true} onOpenChange={closePdfViewer}>
          <DialogContent className="max-w-5xl h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-500" />
                {viewingStatement.filename}
              </DialogTitle>
              <DialogDescription>
                {viewingStatement.bank_name} - {MONTHS.find(m => m.value === viewingStatement.month)?.label} {viewingStatement.year}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 min-h-0 -mx-6 -mb-6">
              {pdfUrl && (
                <iframe
                  src={pdfUrl}
                  className="w-full h-[calc(85vh-120px)] border-0"
                  title="PDF Viewer"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteStatement} onOpenChange={() => setDeleteStatement(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce relevé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le relevé sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
